/**
 * Modal/dialog rendered as a div + React Portal so we control z-index
 * ourselves instead of relying on the browser-managed <dialog> top layer.
 * That makes toast positioning predictable (toasts can sit above modals).
 */
import { type ReactNode, forwardRef, type HTMLAttributes } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Button } from './button'



interface DialogProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  children: ReactNode
  className?: string
}

export function Dialog({ open, onClose, title, description, children, className }: DialogProps) {
  if (!open) return null

  return createPortal(
    // Backdrop: full-viewport black overlay, click to close
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      {/* Stop propagation so clicks inside don't close the dialog */}
      <div
        className={cn(
          'relative flex max-h-[90vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-lg border bg-background shadow-2xl',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || description) && (
          <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b bg-background/95 p-4 backdrop-blur">
            <div>
              {title && <h2 className="text-lg font-semibold">{title}</h2>}
              {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Đóng">
              <X className="h-5 w-5" />
            </Button>
          </div>
        )}
        <div className="flex-1 overflow-auto p-4">{children}</div>
      </div>
    </div>,
    document.body,
  )
}

export const DialogContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('space-y-4', className)} {...props} />,
)
DialogContent.displayName = 'DialogContent'